import React from 'react';
import PropTypes from 'prop-types';
import { analytics } from './snippetSegment';
import { useAuth } from 'react-oidc-context';
import { usePermissions } from '../../user/permissions';
import { useLocation } from 'react-router-dom';
import useAppContext from '../../context/useAppContext';
import { initHubspotHelp } from './snippetHubspot';
import { initJiraFeedback } from './snippetJiraFeedback';
// import { initJiraRequest } from './snippetJiraRequest';
import { initGTMBody } from './snippetGTMBody';
import { initGTMHead } from './snippetGTMHead';

export const ContextTracking = React.createContext(null);

export default function TrackingProvider({ token, ...props }) {
    const { user } = useAuth();
    const permissions = usePermissions();
    const [context, setContext] = React.useState({
        segment: null,
        statusSegment: null,
        initSegment: () => {},
        statusHubspot: null,
        statusJiraFeedback: null,
        statusJiraRequest: null,
        statusGTMHead: null,
        statusGTMBody: null
    });

    const segmentReady = React.useRef(false);

    //Set Segment in Context when it is ready
    const initSegment = React.useCallback(() => {
        if (!context.segment && window.analytics?.initialize && !segmentReady.current) {
            segmentReady.current = true;
            setContext(ctx => {
                return { ...ctx, segment: window.analytics, statusSegment: 'ready' };
            });
        }
    }, [context, window.analytics, segmentReady]);

    const loadSegment = React.useCallback(
        context => {
            let _context = { ...context };
            //Check user capacities and token is not null if we allow loading Segment
            if (!_context.statusSegment && token && permissions.capacities.has('PyvarCapacity.BETA')) {
                try {
                    let _segment = analytics(token);
                    _context.statusSegment = 'loading';
                    _context.initSegment = initSegment;
                    _segment.reset();
                    _segment.identify(user.profile.sub, {
                        'Accounts ID': user.profile.sub
                    });
                    _segment.page();
                    return _context;
                } catch (e) {
                    console.warn('segment could not be loaded');
                    _context.statusSegment = 'disabled';
                    return _context;
                }
            }

            _context.statusSegment = 'disabled';
            return _context;
        },
        [permissions, token, initSegment]
    );

    const loadHubspotHelp = React.useCallback(
        context => {
            let _context = { ...context };
            //Check user capacities if we allow loading HubspotHelp
            if (!_context.statusHubspot && permissions.capacities.has('PyvarCapacity.BETA')) {
                try {
                    initHubspotHelp();
                    _context.statusHubspot = 'ready';
                    return _context;
                } catch (e) {
                    console.warn('hubspot could not be loaded');
                    _context.statusHubspot = 'disabled';
                    return _context;
                }
            }

            _context.statusHubspot = 'disabled';
            return _context;
        },
        [permissions]
    );

    const loadJiraFeedback = React.useCallback(context => {
        let _context = { ...context };
        if (!_context.statusJiraFeedback) {
            try {
                initJiraFeedback();
                _context.statusJiraFeedback = 'ready';
                return _context;
            } catch (e) {
                console.warn('jira feedback could not be loaded');
                _context.statusJiraFeedback = 'disabled';
                return _context;
            }
        }

        _context.statusJiraFeedback = 'disabled';
        return _context;
    }, []);

    // const loadJiraRequest = React.useCallback(
    //     context => {
    //         let _context = { ...context };
    //         //Check user capacities if we allow loading JiraRequest
    //         if (!_context.statusJiraRequest && permissions.capacities.has('PyvarCapacity.ADMIN')) {
    //             try {
    //                 initJiraRequest();
    //                 _context.statusJiraRequest = 'ready';
    //                 return _context;
    //             } catch (e) {
    //                 console.warn('jira request could not be loaded');
    //                 _context.statusJiraRequest = 'disabled';
    //                 return _context;
    //             }
    //         }

    //         _context.statusJiraRequest = 'disabled';
    //         return _context;
    //     },
    //     [permissions]
    // );

    const loadGTMHead = React.useCallback(context => {
        let _context = { ...context };
        if (!_context.statusGTMHead) {
            try {
                initGTMHead();
                _context.statusGTMHead = 'ready';
                return _context;
            } catch (e) {
                console.warn('GTM head could not be loaded');
                _context.statusGTMHead = 'disabled';
                return _context;
            }
        }

        _context.statusGTMHead = 'disabled';
        return _context;
    }, []);

    const loadGTMBody = React.useCallback(context => {
        let _context = { ...context };
        if (!_context.statusGTMBody) {
            try {
                initGTMBody();
                _context.statusGTMBody = 'ready';
                return _context;
            } catch (e) {
                console.warn('GTM body could not be loaded');
                _context.statusGTMBody = 'disabled';
                return _context;
            }
        }

        _context.statusGTMBody = 'disabled';
        return _context;
    }, []);

    React.useEffect(() => {
        let _context = { ...context };
        //The goal is to fully control the behavior of this 'useEffect'.
        // We will pass only once in the 'if' of this 'useEffect'.
        // The logic is based on statuses. In this 'if' we check if our statuses are null and in this case we pass in this condition.
        // On output, we set the context with statuses other than null.
        // It will be 'disabled', 'loading' or 'ready'. And so we'll pass more into this 'if'.
        if (!_context.statusSegment && !_context.statusHubspot && !_context.statusJiraFeedback && !_context.statusGTMHead && !_context.statusGTMBody) {
            _context = loadGTMHead(_context);
            _context = loadHubspotHelp(_context);
            _context = loadSegment(_context);
            _context = loadJiraFeedback(_context);
            if (_context.statusGTMHead === 'ready') {
                _context = loadGTMBody(_context);
            }
            setContext(ctx => {
                return { ...ctx, ..._context };
            });
        }
    }, [context, loadHubspotHelp, loadSegment, loadJiraFeedback]);

    return <ContextTracking.Provider value={{ context, setContext }} {...props} />;
}

TrackingProvider.propTypes = {
    token: PropTypes.string
};

export function useSegment() {
    const { context } = React.useContext(ContextTracking);
    const { user } = useAuth();
    const [{ organization, event }] = useAppContext();

    let segment = context.segment;

    React.useEffect(() => {
        if (!context.segment && context.statusSegment === 'loading') {
            context.initSegment();
        }
    }, [context]);

    const identify = React.useCallback(
        (peopleParams = null) => {
            if (!segment) {
                return null;
            }
            let _peopleParams = { 'Accounts ID': user.profile.sub };
            if (peopleParams) {
                _peopleParams = { ..._peopleParams, ...peopleParams };
            }
            try {
                segment.identify(user.user_id, _peopleParams);
                //Reset traits user in localStorage (use for update people property)
                if (segment.user()) {
                    segment.user().traits({});
                }
            } catch (e) {
                console.warn('segment identify failed');
            }
        },
        [segment, user]
    );

    const track = React.useCallback(
        (trackName = '', eventParams = {}, peopleParams = null) => {
            if (!segment) {
                return null;
            }
            try {
                if (peopleParams) {
                    identify(peopleParams);
                }
                let superProperties = {
                    'Accounts ID': user.user_id,
                    Application: 'WeezTicket nBO',
                    'Organizer ID': organization.pk
                };

                if (event) {
                    superProperties = { ...superProperties, ...{ 'Event ID': event.pk } };
                }

                let params = { ...superProperties, ...eventParams };
                segment.track(trackName, params);
            } catch (e) {
                console.warn('segment track failed');
            }
        },
        [segment, organization, event, context, identify]
    );

    const page = React.useCallback(
        (pageName = '') => {
            if (!segment) {
                return null;
            }
            try {
                segment.page(pageName);
            } catch (e) {
                console.warn('segment page failed');
            }
        },
        [segment]
    );

    return { segment, track, page, identify };
}

export function useTrackingPageAuto() {
    const location = useLocation();
    const { page } = useSegment();
    React.useEffect(() => {
        page(location.pathname);
    }, [location]);
}

export function useTrackingPage(trackName = '', eventParams = {}, peopleParams = null) {
    const { track } = useSegment();
    React.useEffect(() => {
        track(trackName, eventParams, peopleParams);
    }, []);
}
